import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, AlertTriangle, Building2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ActiveDemirbasWarnings = () => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState(null);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${backendUrl}/api/purchasing/active-demirbas-alerts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('fortis_token')}`
        }
      });
      setAlerts(res.data);
    } catch (err) {
      console.error("Uyarılar alınamadı:", err);
      setError("Demirbaş uyarıları yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-4" />
        <p className="text-sm text-gray-500">Demirbaş durumları denetleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Hata</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-3">
          <ClipboardList className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-base font-medium text-gray-700">Demirbaş Uyarısı Bulunmuyor</p>
        <p className="mt-1 text-sm text-gray-400">
          Belirlenen durumlarda (Bozuk, Tamirde vb.) olan herhangi bir demirbaş bulunmuyor.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Demirbaş Uyarıları
          <Badge className="bg-red-100 text-red-700 ml-2 border-red-200">{alerts.length}</Badge>
        </h3>
        <Button variant="outline" size="sm" onClick={fetchAlerts} className="flex items-center gap-2">
          <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {alerts.map((alert, idx) => (
          <div key={idx} className="border border-red-200 bg-red-50/50 rounded-xl p-4 shadow-sm flex flex-col gap-3 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
            
            <div className="flex justify-between items-start pl-2">
              <div>
                <h4 className="font-semibold text-gray-800 text-base">{alert.product_name}</h4>
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Building2 className="w-3 h-3" /> {alert.branch_name}
                </p>
              </div>
              <Badge variant="outline" className="bg-white text-xs border-red-200 text-red-700">
                Kural: {alert.rule_name}
              </Badge>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-red-100 flex items-center justify-between pl-4">
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                  Mevcut Durum
                </span>
                <span className="text-base font-bold text-red-600 capitalize">
                  {alert.status}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                  Seri No
                </span>
                <span className="text-sm font-bold text-gray-800 mt-1">
                  {alert.serial_number || '-'}
                </span>
              </div>
              
              {alert.threshold_days != null && (
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                    Bekleme Süresi
                  </span>
                  <span className="text-lg font-bold text-red-600">
                    {alert.days_in_status} <span className="text-sm font-normal text-red-400">/ {alert.threshold_days} Gün</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActiveDemirbasWarnings;
